import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import type { EditCheckInFormValues } from './types';

const PAIN_LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * Sentinel value for "could not assess" — distinct from null (untouched).
 * Converted to null before sending to the API.
 */
export const PAIN_LEVEL_COULD_NOT_ASSESS = -1;

/** Color for each pain level segment (green→yellow→orange→red) */
const painColor = (level: number, selected: boolean): string => {
  if (!selected) return 'bg-white text-zinc-600 hover:bg-zinc-50';
  if (level <= 2) return 'bg-emerald-500 text-white';
  if (level <= 4) return 'bg-yellow-500 text-white';
  if (level <= 6) return 'bg-amber-500 text-white';
  if (level <= 8) return 'bg-orange-500 text-white';
  return 'bg-red-600 text-white';
};

interface PainLevelSelectorProps {
  disabled?: boolean;
  hasError?: boolean;
}

export const PainLevelSelector: React.FC<PainLevelSelectorProps> = ({
  disabled = false,
  hasError = false,
}) => {
  const { watch, setValue, control } = useFormContext<EditCheckInFormValues>();
  const painLevel = watch('pain_level');

  const isCouldNotAssess = painLevel === PAIN_LEVEL_COULD_NOT_ASSESS;

  const handleSelect = useCallback(
    (level: number) => {
      if (disabled) return;
      setValue('pain_level', level, { shouldDirty: true });
    },
    [disabled, setValue],
  );

  const handleCouldNotAssess = useCallback(() => {
    if (disabled) return;
    setValue('pain_level', PAIN_LEVEL_COULD_NOT_ASSESS, { shouldDirty: true });
  }, [disabled, setValue]);

  return (
    <motion.div
      className={cn(
        'bg-white rounded-lg border px-3 py-2',
        hasError && 'border-red-500',
      )}
      animate={hasError ? { x: [0, -6, 6, -6, 6, 0] } : { x: 0 }}
      transition={hasError ? { duration: 0.4 } : undefined}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-zinc-900 shrink-0">
          Pain level
        </span>
        <div className="flex items-center gap-0.5">
          {PAIN_LEVELS.map((level) => {
            const isSelected = painLevel === level;
            return (
              <button
                key={level}
                type="button"
                disabled={disabled}
                onClick={() => handleSelect(level)}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded border border-zinc-200 text-[11px] font-medium transition-colors',
                  'focus:outline-none focus:ring-1 focus:ring-indigo-500',
                  painColor(level, isSelected),
                  disabled && 'opacity-50 cursor-not-allowed',
                )}
                aria-label={`Pain level ${level}`}
                aria-pressed={isSelected}
              >
                {level}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={handleCouldNotAssess}
          className={cn(
            'text-xs shrink-0 transition-colors',
            isCouldNotAssess
              ? 'font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 ring-1 ring-amber-600/20 ring-inset'
              : 'text-zinc-500 hover:text-zinc-700',
          )}
        >
          Could not assess
        </button>
      </div>
      <AnimatePresence initial={false}>
        {isCouldNotAssess && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-2">
              <Controller
                name="pain_level_note"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={1}
                    placeholder="Why could you not assess the pain level?"
                    className={cn(
                      'text-xs resize-none',
                      hasError && !field.value?.trim() && 'border-red-500',
                    )}
                    disabled={disabled}
                  />
                )}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
